import React, { useEffect, useState } from 'react';
import {
  Link,
  Link as RouterLink,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  Typography,
  Divider,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import {
  handleCustomerSignup,
  handleCustomerSignupNo,
} from '../../../stores/services/authApis';
import { useAlert } from '../../../components/Notification';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

const SignUp = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`; // Replace with your reCAPTCHA site key
    script.async = true;
    script.defer = true;

    document.body.appendChild(script);

    // Hide the reCAPTCHA badge
    const hideBadge = () => {
      const badge = document.querySelector('.grecaptcha-badge') as HTMLElement; // Type assertion
      if (badge) {
        badge.style.display = 'none';
      }
    };

    // show the reCAPTCHA badge
    const showBadge = () => {
      const badge = document.querySelector('.grecaptcha-badge') as HTMLElement; // Type assertion
      if (badge) {
        badge.style.display = 'block';
      }
    };

    // Wait for the script to load before hiding the badge
    script.onload = showBadge;

    // Cleanup function to remove the script
    return () => {
      document.body.removeChild(script);
      hideBadge(); // Hide badge again when unmounting
    };
  }, []);

  const navigate = useNavigate();
  const showAlert = useAlert();
  const location = useLocation();
  const { state } = location;
  const defaultFormData = state?.formData;
  const {
    register,
    formState: { errors },
    handleSubmit,
    clearErrors,
  } = useForm({ defaultValues: defaultFormData });
  const [handleMultipleApi, setHandleMultipleApi] = useState(true);

  //assign mobilenumber
  const handleMobileNumberChange = (event: { target: any; type?: any }) => {
    const inputValue = event.target.value.replace(/\D/g, '');
    event.target.value = inputValue;
    // Update form state with the cleaned value
    register('mobilenumber').onChange(event);
  };

  //remove country code error
  const handleCountryCodeChange = (event: { target: { value: any } }) => {
    if (event.target.value) {
      clearErrors('countrycode');
    }
  };

  // Utility function to safely get the error message
  const getErrorMessage = (error: any) => {
    if (!error) return '';
    if (typeof error?.message === 'string') return error.message;
    return 'Invalid input';
  };

  const signupVerifyNo = async (data: any) => {
    if (!handleMultipleApi) return; // Exit if handleMultipleApi is false

    setHandleMultipleApi(false);
    const recaptchaToken = await window.grecaptcha.enterprise.execute(
      process.env.REACT_APP_RECAPTCHA_SITE_KEY,
      { action: 'SIGNUP' },
    );
    if (recaptchaToken) {
      const paylod = {
        mobileNo: data?.mobilenumber,
        token: recaptchaToken,
        platform: 'web',
      };
      await handleCustomerSignupNo(paylod)
        .then((response) => {
          navigate('/verification', {
            state: {
              mobileNo: data?.mobilenumber,
              page: 'signUp',
              formData: data,
            },
          });
          setHandleMultipleApi(true);
        })
        .catch((error) => {
          showAlert(error?.response?.data?.error || error?.message, 'error');
          setHandleMultipleApi(true);
        });
    } else {
      console.log('Invalid recaptcha Token');
      setHandleMultipleApi(true);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(signupVerifyNo)}>
        <section className="signup_container">
          <div className="signup_header">
            <h4>Sign Up</h4>
          </div>
          <div className="signup_inputfield_containet">
            <div className="signup_inputfield-name">
              <div>
                <InputLabel>First Name</InputLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  {...register('firstname', {
                    required: 'First Name is required',
                  })}
                  error={errors.firstname ? true : false}
                />
                {errors.firstname && (
                  <Typography variant="body2" className="customer_login_error">
                    {getErrorMessage(errors?.firstname)}
                  </Typography>
                )}
              </div>

              <div>
                <InputLabel>Last Name</InputLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  {...register('lastname', {
                    required: 'Last Name is required',
                  })}
                  error={errors.lastname ? true : false}
                />
                {errors.lastname && (
                  <Typography variant="body2" className="customer_login_error">
                    {getErrorMessage(errors?.lastname)}
                  </Typography>
                )}
              </div>
            </div>

            <div className="signup_inputfield-email">
              <InputLabel>email</InputLabel>
              <TextField
                variant="outlined"
                fullWidth
                {...register('email', {
                  // required: 'email is required',
                  pattern: {
                    value: /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
                    message: 'Invalid email address',
                  },
                })}
                placeholder="Ex. johndoe@gmail.com"
                error={errors.email ? true : false}
              />
              {errors.email && (
                <Typography variant="body2" className="customer_login_error">
                  {getErrorMessage(errors?.email)}
                </Typography>
              )}
            </div>
            <div>
              <InputLabel>Mobile Number</InputLabel>
              <div className="signup_inputfield-mobile">
                <Select
                  className="form-countrycode"
                  {...register('countrycode', {
                    required: 'Country Code is required',
                  })}
                  variant="outlined"
                  error={errors.countrycode ? true : false}
                  onChange={handleCountryCodeChange}
                  defaultValue="+91"
                >
                  <MenuItem value="+91">+91</MenuItem>
                </Select>
                <TextField
                  fullWidth
                  {...register('mobilenumber', {
                    required: 'Mobile Number is required',
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: 'Phone number must be 10 digits',
                    },
                  })}
                  inputProps={{
                    maxLength: 10,
                  }}
                  onChange={handleMobileNumberChange}
                  variant="outlined"
                  error={errors.mobilenumber ? true : false}
                />
              </div>
              {errors.mobilenumber && (
                <Typography variant="body2" className="customer_login_error">
                  {getErrorMessage(errors?.mobilenumber)}
                </Typography>
              )}
              {!errors.mobilenumber && errors.countrycode && (
                <Typography variant="body2" className="customer_login_error">
                  {getErrorMessage(errors?.countrycode)}
                </Typography>
              )}
            </div>
          </div>

          <div className="signup_submit">
            <Button type="submit">Get OTP </Button>
            <h5>
              Have an account?
              <span>
                <Link to="/"> Sign in</Link>
              </span>
            </h5>
          </div>
        </section>
      </form>
    </>
  );
};

export default SignUp;
